import { Extension } from "@tiptap/core";
import Suggestion, { SuggestionProps } from "@tiptap/suggestion";
import type { NotificationTagOption } from "./NotificationTag";
import { getNotificationTagDisplayValue } from "./NotificationTag";

type SlashCommandOptions = {
  tags: NotificationTagOption[];
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

type SuggestionItem = NotificationTagOption & {
  displayLabel: string;
};

type SlashSuggestionProps = SuggestionProps<SuggestionItem>;

export const NotificationTagSlashCommand =
  Extension.create<SlashCommandOptions>({
    name: "notificationTagSlashCommand",

    addOptions() {
      return {
        tags: [],
        onMenuOpen: undefined,
        onMenuClose: undefined,
      };
    },

    addProseMirrorPlugins() {
      return [
        Suggestion<SuggestionItem>({
          editor: this.editor,
          char: "/",
          allowSpaces: false,
          items: ({ query }) => {
            const normalizedQuery = query.toLowerCase();
            return this.options.tags
              .flatMap((tag) => {
                const displayLabel =
                  tag.label ?? getNotificationTagDisplayValue(tag.value);
                return displayLabel.toLowerCase().includes(normalizedQuery)
                  ? [{ ...tag, displayLabel }]
                  : [];
              })
              .slice(0, 15);
          },
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertNotificationTag(props.value)
              .run();
          },
          render: () => {
            let component: SuggestionList | null = null;

            return {
              onStart: (props) => {
                component = new SuggestionList({ props });
                this.options.onMenuOpen?.();
              },
              onUpdate(props) {
                component?.update(props);
              },
              onKeyDown(props) {
                if (!component) return false;
                if (props.event.key === "Escape") {
                  component.destroy();
                  return true;
                }
                return component.onKeyDown(props.event);
              },
              onExit: () => {
                component?.destroy();
                component = null;
                this.options.onMenuClose?.();
              },
            };
          },
        }),
      ];
    },
  });

class SuggestionList {
  private element: HTMLDivElement;
  private items: SuggestionItem[] = [];
  private selectedIndex = 0;
  private props: SlashSuggestionProps;
  private stopPropagationHandler = (e: Event) => e.stopPropagation();

  constructor({ props }: { props: SlashSuggestionProps }) {
    this.props = props;
    this.element = document.createElement("div");
    // z-[120] keeps the menu above the FullScreenDrawer panel (z-[100])
    // and its confirm dialogs (z-[110]) — both are body-level portals,
    // so a lower value renders the menu behind the opaque drawer.
    this.element.className =
      "notification-tag-slash-menu z-[120] flex w-64 flex-col rounded-md border border-default-200 bg-white p-1 shadow-lg";

    // Prevent any events from bubbling up to parent elements
    this.element.addEventListener("mousedown", this.stopPropagationHandler);
    this.element.addEventListener("click", this.stopPropagationHandler);
    this.element.addEventListener("mouseup", this.stopPropagationHandler);

    document.body.append(this.element);
    this.update(props);
  }

  update(props: SlashSuggestionProps) {
    const { clientRect, items } = props;
    this.props = props;
    if (!clientRect) {
      this.destroy();
      return;
    }

    this.items = items ?? [];
    if (this.selectedIndex >= this.items.length) {
      this.selectedIndex = 0;
    }

    this.element.innerHTML = "";

    this.items.forEach((item, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = [
        "flex w-full items-center justify-between rounded-md px-2 py-1 text-sm",
        index === this.selectedIndex
          ? "bg-primary-100 text-primary-700"
          : "text-gray-700 hover:bg-default-100",
      ].join(" ");

      button.textContent = item.displayLabel;
      button.addEventListener("mousedown", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.props.command(item);
      });

      this.element.append(button);
    });

    const { left, bottom } = clientRect?.() ?? { left: 0, bottom: 0 };
    // clientRect() returns viewport coordinates — use `fixed` so the
    // menu stays anchored to the caret regardless of scroll position.
    this.element.style.position = "fixed";
    this.element.style.top = `${bottom + 4}px`;
    this.element.style.left = `${left}px`;
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.items.length) {
      return false;
    }

    if (event.key === "ArrowDown") {
      this.selectedIndex = (this.selectedIndex + 1) % this.items.length;
      this.highlightSelection();
      return true;
    }

    if (event.key === "ArrowUp") {
      this.selectedIndex =
        (this.selectedIndex - 1 + this.items.length) % this.items.length;
      this.highlightSelection();
      return true;
    }

    if (event.key === "Enter") {
      const item = this.items[this.selectedIndex];
      if (item) {
        this.props.command(item);
        return true;
      }
    }

    return false;
  }

  private highlightSelection() {
    Array.from(this.element.children).forEach((child, index) => {
      child.className = [
        "flex w-full items-center justify-between rounded-md px-2 py-1 text-sm",
        index === this.selectedIndex
          ? "bg-primary-100 text-primary-700"
          : "text-gray-700 hover:bg-default-100",
      ].join(" ");
    });
  }

  destroy() {
    // Clean up event listeners before removing element
    this.element.removeEventListener("mousedown", this.stopPropagationHandler);
    this.element.removeEventListener("click", this.stopPropagationHandler);
    this.element.removeEventListener("mouseup", this.stopPropagationHandler);
    this.element.remove();
  }
}
